import { useState } from 'react';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import {
  useMerchantApplications,
  useMerchantApplicationDetail,
  useApproveMerchantApplication,
  useRejectMerchantApplication,
} from '../hooks/useMerchantApplications';
import { RejectApplicationModal } from '../components/RejectApplicationModal';
import type { MerchantApplicationSummary } from '../types';

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function DetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { data: detail, isLoading, error } = useMerchantApplicationDetail(id);

  return (
    <Modal isOpen onClose={onClose} size="md">
      <div className="p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">Application Detail</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-muted-foreground text-2xl leading-none"
          >
            &times;
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-10 text-muted-foreground">Loading…</div>
        ) : error ? (
          <div className="text-center py-10 text-red-500">Failed to load application.</div>
        ) : detail ? (
          <div className="space-y-5 text-[14px]">
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Store</p>
              <p className="text-foreground font-[600]">{detail.name}</p>
              <p className="text-muted-foreground">{detail.companyAddress}</p>
            </div>

            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Solution</p>
              <p className="text-foreground">{detail.solutionId}</p>
            </div>

            {detail.latestSigningEvent && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Contract Signed
                </p>
                <p className="text-foreground">
                  {detail.latestSigningEvent.templateNameSnapshot} (v{detail.latestSigningEvent.templateVersionSnapshot})
                </p>
                <p className="text-muted-foreground">
                  by {detail.latestSigningEvent.signerFullName} ({detail.latestSigningEvent.signerEmail})
                  {' · '}
                  {formatDate(detail.latestSigningEvent.signedAt)}
                  {' · '}
                  {detail.latestSigningEvent.acceptanceMethod === 'E_SIGNATURE' ? 'e-signature' : 'explicit accept'}
                </p>
              </div>
            )}

            {detail.pendingBankDetails && Object.keys(detail.pendingBankDetails).length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
                  Banking Details
                </p>
                <div className="bg-muted rounded-[10px] p-3 space-y-1">
                  {Object.entries(detail.pendingBankDetails).map(([key, value]) => (
                    <div key={key} className="flex justify-between gap-4">
                      <span className="text-muted-foreground">{key}</span>
                      <span className="text-foreground font-[600]">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * Merchant application review (PE-1282, backend PE-2077).
 *
 * TCS-admin only: lists merchants who self-onboarded and are awaiting approval,
 * with approve/reject actions. Approving pushes banking details to
 * payment-service, issues a temp password and activates the merchant; rejecting
 * emails the applicant the reason and lets them re-submit.
 */
export function MerchantApplicationsPage() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.MERCHANT_ONBOARDING_MANAGE);

  const { data: applications = [], isLoading, error } = useMerchantApplications();
  const approve = useApproveMerchantApplication();
  const reject = useRejectMerchantApplication();

  const [viewingId, setViewingId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<MerchantApplicationSummary | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const handleApprove = async (application: MerchantApplicationSummary) => {
    if (!window.confirm(`Approve "${application.name}"? This activates the merchant and emails them a login.`)) {
      return;
    }
    setApprovingId(application.id);
    try {
      await approve.mutateAsync(application.id);
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (reason: string) => {
    if (!rejecting) return;
    await reject.mutateAsync({ id: rejecting.id, reason });
    setRejecting(null);
  };

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <div className="mb-6">
        <h1 className="text-[23px] font-bold text-foreground">Merchant Applications</h1>
        <p className="text-[14px] text-muted-foreground mt-1">
          Review merchants who self-onboarded and signed a contract. Approving activates their store;
          rejecting lets them re-submit.
        </p>
      </div>

      {isLoading ? (
        <div className="text-center py-16 text-muted-foreground">Loading applications…</div>
      ) : error ? (
        <div className="text-center py-16 text-red-500">Failed to load applications. Please try again.</div>
      ) : applications.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-[14px] border border-dashed border-[#D9D9D9] rounded-[16px]">
          No applications awaiting approval.
        </div>
      ) : (
        <div className="bg-card rounded-2xl border border-border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted text-left">
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Store</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Solution</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Signed By</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Applied</th>
                <th className="px-6 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {applications.map((application) => (
                <tr key={application.id} className="hover:bg-muted transition-colors align-middle">
                  <td className="px-6 py-4 text-sm font-medium text-foreground">
                    {application.name}
                    <span className="block text-xs text-muted-foreground font-normal">{application.slug}</span>
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{application.solutionId}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {application.latestSigningEvent?.signerFullName ?? '—'}
                  </td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">{formatDate(application.createdAt)}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setViewingId(application.id)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        View
                      </button>
                      {canManage && (
                        <>
                          <Button
                            size="sm"
                            className="h-[30px] px-3"
                            loading={approvingId === application.id}
                            disabled={approvingId !== null}
                            onClick={() => handleApprove(application)}
                          >
                            Approve
                          </Button>
                          <button
                            onClick={() => setRejecting(application)}
                            className="text-xs text-red-600 hover:underline"
                          >
                            Reject
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewingId && <DetailModal id={viewingId} onClose={() => setViewingId(null)} />}

      {rejecting && (
        <RejectApplicationModal
          application={rejecting}
          onClose={() => setRejecting(null)}
          onSubmit={handleReject}
          isSaving={reject.isPending}
        />
      )}
    </div>
  );
}
